import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import FloatingScene from "@/components/FloatingScene";
import { useTheme } from "@/hooks/useTheme";

export default function Login() {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  useTheme();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password, mfaRequired ? mfaCode : null);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "MFA_REQUIRED") {
        setMfaRequired(true);
        toast.info("Enter the 6-digit code from your authenticator");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-background" data-testid="login-page">
      <FloatingScene />
      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_500px]">
        <div className="hidden lg:flex flex-col justify-between p-10 xl:p-14">
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="h-2"
          />

          <div className="max-w-xl space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img src="/brand/relay-wordmark-640.png" alt="Relay CRM" className="h-40 xl:h-48 w-auto select-none mb-8 -ml-2" data-testid="login-hero-logo" draggable={false} />
              <div className="inline-flex items-center gap-2 pill bg-emerald-brand/12 text-emerald-brand mb-5">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-mono uppercase tracking-widest text-[10px]">AI Email Operations</span>
              </div>
              <h1 className="font-display font-semibold text-4xl xl:text-5xl leading-[1.05] tracking-tight">
                A calmer inbox for teams that <span className="italic font-serif text-emerald-brand">reply for a living</span>.
              </h1>
              <p className="text-muted-foreground mt-5 text-base leading-relaxed max-w-md">
                Multi-company mailboxes, department AI agents, approvals, bulk sends,
                and audit-grade logs — one command center for NOC, Billing, Fraud & Support.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="grid grid-cols-2 gap-4"
            >
              <FeatureCard color="emerald" icon={<Sparkles className="h-4 w-4" />} title="Department Agents">
                Draft, approval, hybrid, or automatic reply modes per team.
              </FeatureCard>
              <FeatureCard color="sun" icon={<ShieldCheck className="h-4 w-4" />} title="Tenant Isolated">
                Every record scoped by company. No leakage possible.
              </FeatureCard>
            </motion.div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            v0.2 · Ops Console
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center p-6 sm:p-10"
        >
          <div className="w-full max-w-md card-3d p-7" data-testid="login-card">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-brand">Sign in</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight mt-1">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1.5">Use your admin credentials to access the control center.</p>

            <form onSubmit={onSubmit} className="space-y-4 mt-6">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                  Username or email
                </Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="rounded-xl h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                  Password
                </Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="rounded-xl h-11"
                />
              </div>
              {mfaRequired && (
                <div className="space-y-1.5">
                  <Label htmlFor="mfa" className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                    2FA code
                  </Label>
                  <Input
                    id="mfa"
                    data-testid="login-mfa-input"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoFocus
                    className="rounded-xl h-11 font-mono tracking-widest text-center text-lg"
                    placeholder="000000"
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl h-11 text-sm font-semibold shadow-lg shadow-primary/30"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>Sign in <ArrowRight className="h-4 w-4 ml-1.5" /></>
                )}
              </Button>
              <div className="text-[11px] text-muted-foreground pt-3 border-t border-border font-mono">
                Default super admin: <span className="text-foreground">admin</span> · password from install
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, children, color = "emerald" }) {
  const map = {
    emerald: "bg-emerald-brand/12 text-emerald-brand",
    sun: "bg-sun/20 text-yellow-700",
  };
  return (
    <motion.div whileHover={{ y: -3 }} className="card-3d p-4">
      <div className={`h-8 w-8 rounded-lg grid place-items-center ${map[color]} mb-3`}>{icon}</div>
      <div className="text-sm font-semibold">{title}</div>
      <p className="text-xs text-muted-foreground leading-relaxed mt-1">{children}</p>
    </motion.div>
  );
}
